import { useState, useEffect } from 'react';
import { Copy, Check, ThumbsUp, ThumbsDown, RefreshCw, Star, Volume2, VolumeX } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { submitFeedback, getFeedback, deleteFeedback, FeedbackType } from '@/services/feedbackService';
import { saveMessage, deleteSavedMessage, getSavedMessageByMessageId, getAllTags } from '@/services/savedMessagesService';
import { SaveMessageDialog } from '@/components/saved/SaveMessageDialog';
import { useTextToSpeech } from '@/hooks/useTextToSpeech';
import { useToast } from '@/hooks/use-toast';
import { GammaGenerateButtons } from './GammaGenerateButtons';

interface MessageActionsProps {
  content: string;
  messageId: string;
  chatId: string;
  isLastAssistantMessage: boolean;
  onRegenerate?: () => void;
  mode?: 'funcionarios' | 'oposiciones' | 'oposiciones_c1' | 'oposiciones_guardia_civil' | 'oposiciones_policia_nacional' | 'oposiciones_iipp' | 'oposiciones_c2' | 'oposiciones_generico';
}

export function MessageActions({ 
  content, 
  messageId, 
  chatId, 
  isLastAssistantMessage,
  onRegenerate,
  mode
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const { user } = useAuth();
  const { toast } = useToast();
  const { isSpeaking, speak, stop, isSupported: ttsSupported } = useTextToSpeech();

  // Load existing feedback and saved status on mount
  useEffect(() => {
    if (messageId && user?.id) {
      loadFeedback();
      loadSavedStatus();
    }
  }, [messageId, user?.id]);

  const loadSavedStatus = async () => {
    if (!messageId || !user?.id) return;
    
    try {
      const saved = await getSavedMessageByMessageId(user.id, messageId);
      if (saved) {
        setIsSaved(true);
        setSavedId(saved.id);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const loadFeedback = async () => {
    if (!messageId || !user?.id) return;
    
    try {
      const existingFeedback = await getFeedback(messageId, user.id);
      if (existingFeedback) {
        setFeedback(existingFeedback.feedback_type);
      }
    } catch (error) {
      // Silent fail for loading
    }
  };

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const handleFeedback = async (type: FeedbackType) => {
    if (!messageId || !user?.id || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (feedback === type) {
        // Toggle off - delete feedback
        await deleteFeedback(messageId, user.id);
        setFeedback(null);
      } else {
        // Submit new feedback
        await submitFeedback(messageId, user.id, type);
        setFeedback(type);
        
        toast({
          title: type === 'positive' ? '¡Gracias!' : 'Gracias por tu feedback',
          description: type === 'positive' 
            ? 'Tu valoración nos ayuda a mejorar' 
            : 'Trabajaremos para mejorar nuestras respuestas',
        });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar tu valoración',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!onRegenerate || isRegenerating) return;
    
    setIsRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSaveClick = async () => {
    if (!user?.id) return;
    
    if (isSaved && savedId) {
      // Unsave
      try {
        await deleteSavedMessage(savedId);
        setIsSaved(false);
        setSavedId(null);
        toast({
          title: 'Eliminado de guardados',
        });
      } catch (error) {
        console.error('Error removing saved message:', error);
        toast({
          title: 'Error',
          description: 'No se pudo eliminar de guardados',
          variant: 'destructive',
        });
      }
    } else {
      // Load existing tags and show dialog
      try {
        const tags = await getAllTags(user.id);
        setExistingTags(tags);
        setShowSaveDialog(true);
      } catch (error) {
        setShowSaveDialog(true);
      }
    }
  };

  const handleSaveMessage = async (data: { note: string; tags: string[] }) => {
    if (!user?.id || !messageId) return;
    
    try {
      const saved = await saveMessage(user.id, messageId, chatId, data);
      setIsSaved(true);
      setSavedId(saved.id);
      setShowSaveDialog(false);
      toast({
        title: '¡Guardado!',
        description: 'Puedes encontrarlo en la sección "Guardados"',
      });
    } catch (error: any) {
      console.error('Error saving message:', error);
      if (error?.code === '23505') {
        toast({
          title: 'Ya guardado',
          description: 'Esta respuesta ya está en tus guardados',
        });
      } else {
        toast({
          title: 'Error',
          description: 'No se pudo guardar la respuesta',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="flex gap-1 items-center">
      {/* Copy button */}
      <button
        onClick={handleCopy}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        title="Copiar respuesta"
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 text-green-600" />
            <span className="text-green-600 font-medium">Copiado</span>
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" />
            <span>Copiar</span>
          </>
        )}
      </button>

      {/* Listen button (TTS) */}
      {ttsSupported && (
        <>
          <div className="w-px h-4 bg-border mx-1" />
          <button
            onClick={() => isSpeaking ? stop() : speak(content)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
              isSpeaking
                ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50'
                : 'text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/30'
            }`}
            title={isSpeaking ? 'Detener lectura' : 'Escuchar respuesta'}
          >
            {isSpeaking ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            <span>{isSpeaking ? 'Parar' : 'Escuchar'}</span>
          </button>
        </>
      )}

      {/* Divider */}
      <div className="w-px h-4 bg-border mx-1" />

      {/* Save button */}
      <button
        onClick={handleSaveClick}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
          isSaved
            ? 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50'
            : 'text-muted-foreground hover:text-yellow-600 hover:bg-yellow-50 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/30'
        }`}
        title={isSaved ? 'Quitar de guardados' : 'Guardar respuesta'}
      >
        <Star className={`h-3.5 w-3.5 ${isSaved ? 'fill-current' : ''}`} />
      </button>

      {/* Thumbs Up */}
      <button
        onClick={() => handleFeedback('positive')}
        disabled={isSubmitting}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
          feedback === 'positive'
            ? 'text-green-600 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-900/30'
            : 'text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400'
        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="Respuesta útil"
      >
        <ThumbsUp className={`h-3.5 w-3.5 ${feedback === 'positive' ? 'fill-green-600 dark:fill-green-400' : ''}`} />
      </button>

      {/* Thumbs Down */}
      <button
        onClick={() => handleFeedback('negative')}
        disabled={isSubmitting}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
          feedback === 'negative'
            ? 'text-red-500 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30'
            : 'text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:text-red-400'
        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="Respuesta mejorable"
      >
        <ThumbsDown className={`h-3.5 w-3.5 ${feedback === 'negative' ? 'fill-red-500 dark:fill-red-400' : ''}`} />
      </button>

      {/* Regenerate - only show for last assistant message */}
      {isLastAssistantMessage && onRegenerate && (
        <>
          <div className="w-px h-4 bg-border mx-1" />
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ${
              isRegenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Regenerar respuesta"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
            <span>Regenerar</span>
          </button>
        </>
      )}

      {/* Gamma Generate Buttons - all oposiciones bodies */}
      {typeof mode === 'string' && mode.startsWith('oposiciones') && (
        <GammaGenerateButtons content={content} />
      )}

      {/* Save Dialog */}
      <SaveMessageDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onSave={handleSaveMessage}
        existingTags={existingTags}
      />
    </div>
  );
}
